import { useCallback } from 'react';
import { usePlanLimitsContext } from '@/context/PlanLimitsContext';

const DEFAULT_UPGRADE_MESSAGE = "You've reached your plan's limit. Upgrade your plan to unlock more.";

const FEATURE_UPGRADE_MESSAGES = {
  in_app_messaging: 'Messaging is not available on your plan. Upgrade your plan to unlock it.',
  full_analytics: 'Full analytics is not available on your plan. Upgrade your plan to unlock it.',
  position_sort_filter: 'Sorting and filtering positions is not available on your plan. Upgrade your plan to unlock it.',
};

// Subs (TOCs) are not plan buyers — never show them upsell copy or an Upgrade
// link. Gating on the Sub side is keyed off the school's plan, so the message
// points them at their school admin instead of the pricing page.
const SUB_FEATURE_MESSAGES = {
  in_app_messaging: 'Messaging is not available because of a plan limitation. Please contact your school admin.',
};

/**
 * Centralized plan-limits/feature-gating hook, backed by PlanLimitsContext.
 * `hasFeature`/`isAtLimit` check the authenticated employer's own plan; use
 * `planHasFeature(planId, featureKey)` on the Sub (TOC) side, where gating is
 * keyed off a specific school's plan rather than the viewer's own.
 */
export function usePlanLimits() {
  const { plan, features, limits, counts, trial, plans, plansById, isLoading, refreshStatus } = usePlanLimitsContext();

  const planHasFeature = useCallback((planId, featureKey) => {
    const targetPlan = plansById[planId] ?? plansById['head_start'];
    return !!targetPlan?.features?.includes(featureKey);
  }, [plansById]);

  const hasFeature = useCallback((featureKey) => {
    return features.includes(featureKey);
  }, [features]);

  const isAtLimit = useCallback((resourceKey) => {
    const limit = limits[resourceKey];
    const count = counts[resourceKey];
    if (limit === null || limit === undefined || limit === 'unlimited') return false;
    if (typeof count !== 'number' || typeof limit !== 'number') return false;
    return count >= limit;
  }, [limits, counts]);

  const upgradeTooltipMessage = useCallback((featureKey) => {
    return FEATURE_UPGRADE_MESSAGES[featureKey] || DEFAULT_UPGRADE_MESSAGE;
  }, []);

  // Sub-side counterpart of upgradeTooltipMessage: neutral copy, no upsell.
  const subFeatureMessage = useCallback((featureKey) => {
    return SUB_FEATURE_MESSAGES[featureKey]
      || 'This feature is not available because of a plan limitation. Please contact your school admin.';
  }, []);

  return {
    plan,
    features,
    limits,
    counts,
    trial,
    plans,
    plansById,
    isLoading,
    hasFeature,
    planHasFeature,
    isAtLimit,
    upgradeTooltipMessage,
    subFeatureMessage,
    refreshStatus,
  };
}
